/** Dates the operator puts on the compliance calendar. A blank date means "not this year." */

export type ComplianceCalendarEvent = {
  id: string;
  title: string;
  date: string;
  skipped?: boolean;
};

export type CalendarFrequency = 'monthly' | 'quarterly' | 'annual';

export type ComplianceCalendarSuggestion = {
  id: string;
  title: { en: string; es: string };
  hint: { en: string; es: string };
  /** How often the work repeats. The date itself is still one the operator saves. */
  frequency: CalendarFrequency;
};

const suggestion = (
  id: string,
  enTitle: string,
  esTitle: string,
  enHint: string,
  esHint: string,
  frequency: CalendarFrequency = 'annual',
): ComplianceCalendarSuggestion => ({
  id,
  title: { en: enTitle, es: esTitle },
  hint: { en: enHint, es: esHint },
  frequency,
});

export const frequencyLabel = (frequency: CalendarFrequency, language: 'en' | 'es'): string => {
  const labels = {
    monthly: { en: 'Monthly', es: 'Mensual' },
    quarterly: { en: 'Quarterly', es: 'Trimestral' },
    annual: { en: 'Annual', es: 'Anual' },
  };
  return labels[frequency][language];
};

export const CALENDAR_SUGGESTIONS: Record<string, ComplianceCalendarSuggestion[]> = {
  'dod-contracts-compliance': [
    suggestion('sam-renewal', 'SAM.gov renewal', 'Renovación de SAM.gov', 'Use the expiration date on the Active record. Do not guess a date.', 'Use la fecha de vencimiento del registro Active. No adivine una fecha.'),
    suggestion('tx-franchise-report', 'Texas franchise report and Public Information Report', 'Reporte de franquicia de Texas y Reporte de Información Pública', 'The Comptroller due date is May 15, or the next business day when that date is a weekend or holiday. Change it if the notice says otherwise.', 'El vencimiento del Contralor es el 15 de mayo, o el siguiente día hábil si cae en fin de semana o feriado. Cámbielo si el aviso dice otra fecha.'),
    suggestion('cmmc-affirmation', 'CMMC Level 1 affirmation', 'Afirmación de CMMC Nivel 1', 'A senior official affirms in SPRS once a year. Enter the date you will affirm. This page does not file SPRS.', 'Un funcionario superior afirma en SPRS una vez al año. Anote la fecha en que afirmará. Esta página no presenta SPRS.'),
    suggestion('insurance-certificate', 'Insurance certificate renewal', 'Renovación del certificado de seguro', 'Use the date on the certificate of insurance. Do not invent a policy date.', 'Use la fecha del certificado de seguro. No invente una fecha de póliza.'),
  ],
  'website-compliance': [
    suggestion('site-rescan', 'Next homepage scan', 'Próximo escaneo de la homepage', 'Use the date you will run the next scan. Do not invent a finding.', 'Use la fecha en que hará el próximo escaneo. No invente un hallazgo.', 'quarterly'),
    suggestion('policy-review', 'Privacy, cookie, and terms review', 'Revisión de privacidad, cookies y términos', 'Use the date those pages should be read again.', 'Use la fecha en que esas páginas deben leerse de nuevo.'),
    suggestion('domain-renewal', 'Domain renewal', 'Renovación del dominio', 'Use the expiration date from the registrar. Do not guess it.', 'Use la fecha de vencimiento del registrador. No la adivine.'),
  ],
  'cloud-compliance': [
    suggestion('cloud-review', 'Read-only cloud review', 'Revisión de nube de solo lectura', 'Use the date of the next review of the access the client granted.', 'Use la fecha de la próxima revisión del acceso que concedió el cliente.', 'quarterly'),
    suggestion('key-rotation', 'Key rotation', 'Rotación de claves', 'Use the date the client rotates keys. Do not invent a key.', 'Use la fecha en que el cliente rota las claves. No invente una clave.'),
  ],
  'education-compliance': [
    suggestion('ipeds', 'IPEDS collection', 'Colección IPEDS', 'Use the collection date on the current IPEDS calendar.', 'Use la fecha de la colección en el calendario IPEDS vigente.'),
    suggestion('clery', 'Annual security report', 'Reporte anual de seguridad', 'The federal due date is October 1. Change it if the campus calendar says otherwise.', 'El vencimiento federal es el 1 de octubre. Cámbielo si el calendario del campus dice otra fecha.'),
    suggestion('foreign-gift', 'Foreign gift report', 'Reporte de regalos extranjeros', 'Use the date the campus files that report. Do not invent a gift.', 'Use la fecha en que el campus presenta ese reporte. No invente un regalo.'),
  ],
  'wills-trusts-compliance': [
    suggestion('plan-review', 'Read the plan again', 'Volver a leer el plan', 'Use the date after a marriage, divorce, birth, death, or a move. Do not invent a court date.', 'Use la fecha después de un matrimonio, divorcio, nacimiento, muerte o mudanza. No invente una fecha de tribunal.'),
    suggestion('signing-appointment', 'Signing appointment', 'Cita de firma', 'Use the date the lawyer sets. Do not sign before that review.', 'Use la fecha que fije el abogado. No firme antes de esa revisión.'),
  ],
  'business-creation-compliance': [
    suggestion('state-report', 'State recurring report', 'Reporte periódico del estado', 'Use the due date on the state notice. For Texas, the franchise report is May 15 unless the notice says otherwise.', 'Use el vencimiento del aviso del estado. En Texas, el reporte de franquicia es el 15 de mayo salvo que el aviso diga otra fecha.'),
    suggestion('agent-renewal', 'Registered agent renewal', 'Renovación del agente registrado', 'Use the renewal date from the agent. Do not guess it.', 'Use la fecha de renovación del agente. No la adivine.'),
  ],
  'ofac-compliance': [
    suggestion('list-refresh', 'SDN and CAPTA list check', 'Revisión de listas SDN y CAPTA', 'Use the date of the next check after a successful refresh. Do not invent a match.', 'Use la fecha de la próxima revisión después de una actualización correcta. No invente una coincidencia.', 'monthly'),
  ],
  'ai-compliance': [
    suggestion('ai-inventory-review', 'AI tool inventory review', 'Revisión del inventario de herramientas de IA', 'Use the date the inventory should be read again. Do not paste prompts or model output.', 'Use la fecha en que el inventario debe leerse de nuevo. No pegue prompts ni salidas del modelo.'),
  ],
};

export const GOVERNMENT_CALENDAR_SUGGESTIONS = CALENDAR_SUGGESTIONS['dod-contracts-compliance'];

export const calendarTaskId = (kind: string): string => (
  kind === 'dod-contracts-compliance' ? 'compliance-calendar-dates' : `${kind}-calendar`
);

export const calendarSuggestionsFor = (kind: string): ComplianceCalendarSuggestion[] => (
  CALENDAR_SUGGESTIONS[kind] || []
);

/** A counseling date, appointment, or other dated proof belongs on the calendar. */
export const evidenceAsksForADate = (hint: string): boolean => (
  /\b(date|fecha|appointment|meeting|cita)\b/i.test(hint)
);

export const calendarDateLabel = (hint: string, language: 'en' | 'es'): string => {
  if (/\b(counsel\w*|appointment|meeting|cita)\b/i.test(hint)) {
    return language === 'es' ? 'Fecha de la cita' : 'Meeting date';
  }
  return language === 'es' ? 'Fecha' : 'Date';
};

export const upsertCalendarDate = (
  events: ComplianceCalendarEvent[],
  next: { id: string; title: string; date: string },
): ComplianceCalendarEvent[] => {
  const rest = events.filter((event) => event.id !== next.id);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(next.date)) {
    return rest;
  }
  return [...rest, { id: next.id, title: next.title, date: next.date }];
};

export const parseCalendarEvents = (value: unknown): ComplianceCalendarEvent[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const row = entry as Partial<ComplianceCalendarEvent>;
    const id = String(row.id || '').trim();
    const title = String(row.title || '').trim();
    const date = String(row.date || '').trim();
    if (!id || !title) {
      return [];
    }
    if (!row.skipped && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return [];
    }
    return [{ id, title, date: row.skipped ? '' : date, skipped: Boolean(row.skipped) }];
  });
};

export const monthCells = (year: number, monthIndex: number): Array<string | null> => {
  const first = new Date(year, monthIndex, 1);
  const days = new Date(year, monthIndex + 1, 0).getDate();
  const cells: Array<string | null> = Array.from({ length: first.getDay() }, () => null);
  for (let day = 1; day <= days; day += 1) {
    const month = String(monthIndex + 1).padStart(2, '0');
    const date = String(day).padStart(2, '0');
    cells.push(`${year}-${month}-${date}`);
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
};
